import { useQuery } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DetailAccordionContainer } from '~/components/DetailAccordion';
import { UsageTypeChip } from '~/components/UsageTypeChip';
import { SeparatedDataGrid } from '~/components/data-grid';
import { DataGridPagination } from '~/components/data-grid/pagination';
import { EmptyState } from '~/components/empty-state';
import { SearchBar } from '~/components/forms/search-bar';
import { AuthorizedLink, pathFor } from '~/components/links/AuthorizedLink';
import { useAppContext } from '~/contexts';
import type { ContentOrderBy } from '~/generated/graphql';
import { useLink } from '~/hooks/link';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import AddAppsIllustration from '~/images/illustrations/add-apps.svg';
import {
  MediaItemUsageDocument,
  type MediaItemUsage__App as UsageApp,
  type MediaItemUsage__Design as UsageDesign,
  type MediaItemUsage__Playlist as UsagePlaylist,
  type MediaItemUsage__Show as UsageShow,
} from './MediaItemUsage.generated';

type UsageRow = UsageApp | UsageDesign | UsagePlaylist | UsageShow;

const folderOf = (row: UsageRow) =>
  row.__typename === 'ContentItem'
    ? row.contentFolder
    : row.__typename === 'Design'
    ? row.designFolder
    : row.__typename === 'Playlist'
    ? row.playlistFolder
    : row.showFolder;

const linkableRow = (row: UsageRow) => {
  const folderId = folderOf(row).id;
  return { ...row, contentFolderId: folderId, playlistFolderId: folderId, showFolderId: folderId };
};

const useColumns = (): GridColDef<UsageRow>[] => {
  const { t } = useTranslation();

  return [
    {
      field: 'type',
      width: 96,
      headerName: t('common.labels.type'),
      resizable: false,
      sortable: false,
      renderCell: ({ row }) => <UsageTypeChip typename={row.__typename} />,
    },
    {
      field: 'name',
      flex: 2,
      minWidth: 140,
      headerName: t('common.labels.name'),
      resizable: true,
      renderCell: ({ row }) => (
        <AuthorizedLink authorized={row.canUpdate.value} entity={linkableRow(row)} />
      ),
    },
    {
      field: 'location',
      flex: 1,
      minWidth: 120,
      headerName: t('pages.content.media_details.usage.location_column'),
      resizable: true,
      sortable: false,
      renderCell: ({ row }) => {
        const folder = folderOf(row);
        return <AuthorizedLink authorized={folder.canUpdate.value} entity={folder} />;
      },
    },
  ];
};

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={AddAppsIllustration}
      header={t('pages.content.media_details.usage.no_content_items_header')}
      description={t('pages.content.media_details.usage.no_content_items_description')}
    />
  );
};

export const MediaItemUsage = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const params = useParams<{ mediaItemId: string }>();
  const mediaItemId = parseInt(params.mediaItemId ?? '');
  const navigate = useNavigate();
  const link = useLink();
  const { handleOutOfBounds, paginationModel, setPageParams } = usePaginationParams();
  const columns = useColumns();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const { orderBy, setOrderByParams, sortModel } = useOrderByParams<ContentOrderBy>();
  const { data, loading } = useQuery(MediaItemUsageDocument, {
    variables: {
      networkId: currentNetwork.id,
      mediaItemId,
      page: paginationModel.page + 1,
      perPage: paginationModel.pageSize,
      search,
      ...(orderBy && { orderBy }),
    },
    onCompleted: (d) => handleOutOfBounds(d.network?.mediaItem?.usages.pageInfo),
  });

  const rows = data?.network?.mediaItem?.usages.nodes ?? [];
  const totalCount = useTotalCount(data?.network?.mediaItem?.usages.pageInfo.nodesCount);

  return (
    <DetailAccordionContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <SearchBar
          onChange={(value) =>
            setSearchParams((params) => {
              params.set('search', value);
              params.set('page', '1');
              return params;
            })
          }
          placeholder={t('pages.content.media_details.usage.search_placeholder')}
          search={search}
        />
        <Typography variant="subtitle1">
          {t('pages.content.media_details.usage.used_in_places_plural', { count: totalCount })}
        </Typography>
      </Box>
      <SeparatedDataGrid
        columns={columns}
        loading={loading}
        getRowId={(row) => `${row.__typename}-${row.id}`}
        onPaginationModelChange={(m) => setPageParams({ page: m.page + 1, perPage: m.pageSize })}
        onRowClick={({ row }: GridRowParams<UsageRow>) => navigate(link(pathFor(linkableRow(row))))}
        onSortModelChange={(m) => setOrderByParams(m.at(0) ?? null)}
        paginationMode="server"
        paginationModel={paginationModel}
        rowCount={totalCount}
        rows={rows}
        slots={{
          columnResizeIcon: () => null,
          noRowsOverlay: NoRowsOverlay,
          pagination: DataGridPagination,
        }}
        sortingMode="server"
        sortModel={sortModel}
      />
    </DetailAccordionContainer>
  );
};
